import React, { useEffect, useMemo, useState } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { ModalsController } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import { ThemeProvider as TailwindThemeProvider } from 'components/theme-provider'
import { AzureAdProvider } from 'lib/react-aad'
import { PersistGate } from 'redux-persist/integration/react'
import { ThemeProvider } from 'styled-components'
import { theme } from 'styles/theme'
import { AuthSessionContext, MaybeAuthSession } from 'utils/AuthContext'
import { ConfigContext, ConfigData } from 'utils/ConfigContext'
import { UIVersionProvider } from 'utils/UIVersionContext'

import { antConfig, configFeatures, IConfig } from './Config'
import { fetchInitialState } from './routes/Login/loginActions'
import { Routing } from './Routing'

import './App.scss'

import { DataLayoutProvider } from '@/utils/DataLayoutContext'
import { ConfigProvider } from 'antd'
import { isErrorSilent } from 'api/silentErrors'
import { useAppDispatch } from 'store'
import { persistor, PREFERRED_DATE_FORMAT, store } from 'store/store'
import { ApiClient, ApiClientContext, isUnAuthorized, useApiClient } from 'utils/api/ApiClient'

import { HttpStatus } from './api/httpStatus'
import { TokenStorage } from './api/tokenStorage'
import { errorToast, ToastProvider } from './components/Toast/Toast'
import { showToast } from './components/Toast/toast-provider'
import { TooltipProvider } from './components/ui/tooltip'
import { IdentityApiClient } from './utils/api/identityApi'
import { WalletsApiClient } from './utils/api/walletsApiClient'
import { ConfirmationContext, MaybeRequireConfirmation } from './utils/ConfirmationContext'
import { links } from './utils/featureToggles'
import SignalRHub from './utils/Hub'
import { WebSocketContext, WebSocketData } from './utils/WebSocketContext'

interface ErrorObject {
  code?: string
  message?: string
  reason?: string
  Code?: string
  Message?: string
  childErrors?: ErrorObject[]
  ChildErrors?: ErrorObject[]
}

export interface ApiResponse {
  response?: {
    data?: ErrorObject
    status?: number
    config?: Record<string, any>
  }
  config?: {
    url?: string
  }
}

const msalConfig = {
  auth: {
    redirectUri: window.env.APP_AAD_REDIRECTURI || '',
    authority: window.env.APP_AAD_AUTHORITY || '',
    clientId: window.env.APP_AAD_CLIENTID || '',
  },
}

export const App = () => {
  const configData = useMemo(() => {
    const features = {
      [configFeatures.AzureActiveDirectory]: window.env.APP_AAD_ENABLED === 'true',
    }
    const config = { id: 'default', config: { features } } as IConfig
    return new ConfigData(config)
  }, [])

  return (
    <Provider store={store}>
      <ConfigContext.Provider value={configData}>
        <UIVersionProvider>
          <TailwindThemeProvider defaultTheme='system' storageKey='tickmill-ui-theme'>
            <AzureAdProvider config={msalConfig}>
              <BrowserRouter>
                <ThemeProvider theme={theme}>
                  <DataLayoutProvider>
                    <TooltipProvider>
                      <PersistGate persistor={persistor}>
                        {(bootstrapped) => {
                          if (bootstrapped) return <Session />
                          return <Spinner />
                        }}
                      </PersistGate>
                    </TooltipProvider>
                  </DataLayoutProvider>
                </ThemeProvider>
              </BrowserRouter>
            </AzureAdProvider>
          </TailwindThemeProvider>
        </UIVersionProvider>
      </ConfigContext.Provider>
    </Provider>
  )
}

const apiClient = new ApiClient()

const Session: React.FC = () => {
  const [session, setSession] = useState<MaybeAuthSession>(null)
  const [loading, setLoading] = useState(true)
  const [requireConfirmation, setRequireConfirmationData] = useState<MaybeRequireConfirmation>(null)
  const identityApiClient = useApiClient(IdentityApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const navigate = useNavigate()

  const websocketData = useMemo(() => {
    // todo enable when will be working
    if (process.env.NODE_ENV === 'development') {
      return { hub: undefined }
    }
    return new WebSocketData(new SignalRHub())
  }, [])

  const dispatch = useAppDispatch()

  useEffect(() => {
    ;(async () => {
      // do not fetch session for reset/forgot-password path
      if (
        window.location.pathname.includes('reset-password') ||
        window.location.pathname.includes('forgot-password')
      ) {
        setLoading(false)
        return undefined
      }
      try {
        const newSession = await identityApiClient.getUserBySession()
        const permissions = await identityApiClient.getAuthUserPermission()
        setSession({ user: newSession, permissions })
        dispatch(fetchInitialState(identityApiClient, walletsApiClient))
        localStorage.setItem(PREFERRED_DATE_FORMAT, newSession.dateFormatType.name)
        setLoading(false)
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  apiClient.errorHandler = async (error) => {
    const response = (error?.response as ApiResponse) || {}
    const title = 'Something went wrong!'
    let subtitle = `Request failed with status code ${response.response?.status}`

    function formatError(errorObj: ErrorObject, numbering: number[] = [], indent = 0) {
      const pad = '  '.repeat(indent)
      const message = errorObj.message || errorObj.Message || errorObj.reason || ''

      let prefixStr = ''
      if (numbering.length > 0) {
        prefixStr = numbering.join('.')
        if (numbering.length === 1) {
          prefixStr += '.'
        }
        prefixStr += ' '
      }

      const line = pad + prefixStr + message
      const lines = [line]

      const children = errorObj.childErrors || errorObj.ChildErrors
      if (children && Array.isArray(children) && children.length) {
        if (indent === 0 && numbering.length === 0) {
          lines.push('<hr />')
        }
        children.forEach((child, index) => {
          const childNumbering = [...numbering, index + 1]
          const childStr = formatError(child, childNumbering, indent + 1)
          lines.push(childStr)
        })
      }

      return lines.join('<br />')
    }

    if (response.response?.data) {
      subtitle = formatError(response.response.data)
    }

    if (isErrorSilent(response)) return

    const errorCode = response?.response?.data?.code || response?.response?.data?.Code
    const errorMsg = response?.response?.data?.message || response?.response?.data?.Message

    if (errorCode === 'client_cannot_be_blocked') {
      return setRequireConfirmationData({
        ...requireConfirmation,
        code: errorCode,
        message: errorMsg,
      })
    }

    if (errorCode === 'trading_account_balance_minimum_for_trading_account_type_change') {
      return setRequireConfirmationData({
        ...requireConfirmation,
        code: errorCode,
        message: errorMsg,
      })
    }

    if (!response.response) {
      subtitle = `It seems you're offline!`
    }
    if (response.config?.url?.includes('refresh')) {
      setSession(null)
      TokenStorage.clear()
      persistor.purge()
      navigate(links.login.url)

      return showToast(
        errorToast({
          title: 'Forbidden',
          subtitle: "Cannot refresh token, you're going to be sign out.",
        })
      )
    }
    if (isUnAuthorized(response?.response?.status)) {
      TokenStorage.clear()
      persistor.purge()
      navigate(links.login.url)
    }

    if (response.response?.config?.data?.includes('skipError')) {
      return
    }

    if (response.response?.status === HttpStatus.FORBIDDEN) {
      return showToast(
        errorToast({
          title: 'Forbidden',
          subtitle: "You don't have access to this resource.",
        })
      )
    }

    if (!isUnAuthorized(response?.response?.status)) {
      return showToast(
        errorToast({
          title,
          dangerouslySetInnerHTML: { __html: subtitle },
        })
      )
    }
  }

  return (
    <Loading isLoading={loading}>
      <ConfigProvider {...antConfig}>
        <ApiClientContext.Provider value={apiClient}>
          <WebSocketContext.Provider value={websocketData}>
            <ToastProvider>
              <ConfirmationContext.Provider
                value={[requireConfirmation, setRequireConfirmationData]}
              >
                <AuthSessionContext.Provider value={[session, setSession]}>
                  <Routing />
                  <ModalsController />
                </AuthSessionContext.Provider>
              </ConfirmationContext.Provider>
            </ToastProvider>
          </WebSocketContext.Provider>
        </ApiClientContext.Provider>
      </ConfigProvider>
    </Loading>
  )
}
